import { useState, useCallback } from 'react';
import { ArrowUp, ArrowDown, MessageSquare, AlertTriangle, AlertCircle, Calendar, BarChart3, ArrowRightLeft, Users, Filter } from 'lucide-react';
import { ContextMenu } from '../ui/ContextMenu';
import type { ContextMenuItem } from '../ui/ContextMenu';
import type { AgedStockItem, AgedStockSortField, AgedStockSortOrder, AgedStockFilters } from '../../types/agedStock';

export interface AgedStockFilterAction {
    filterKey: keyof AgedStockFilters;
    value: string;
    label: string;
}

interface AgedStockTableProps {
    items: AgedStockItem[];
    sortBy: AgedStockSortField;
    sortOrder: AgedStockSortOrder;
    canEdit: boolean;
    onSort: (field: AgedStockSortField) => void;
    onShowDetail: (item: AgedStockItem) => void;
    onShowTransactions: (item: AgedStockItem) => void;
    onShowBuyers: (item: AgedStockItem) => void;
    onToggleFlag: (item: AgedStockItem, flagType: 'Expired' | 'QualityIssue') => void;
    onFilterByValue?: (action: AgedStockFilterAction) => void;
    selectedKey: string | null;
}

function formatDate(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('de-CH');
}

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value);
}

function ageColorClass(ageDays: number | null): string {
    if (ageDays == null) return 'text-gray-500';
    if (ageDays > 720) return 'text-red-700 font-semibold';
    if (ageDays > 365) return 'text-orange-600 font-medium';
    if (ageDays > 180) return 'text-yellow-600';
    return 'text-green-700';
}

function ageBucketBadge(bucket: string): string {
    if (bucket === '>720') return 'bg-red-100 text-red-800';
    if (bucket === '366-720') return 'bg-orange-100 text-orange-800';
    if (bucket === '181-365') return 'bg-yellow-100 text-yellow-800';
    if (bucket === '91-180') return 'bg-blue-100 text-blue-800';
    return 'bg-green-100 text-green-800';
}

function expiryBadge(item: AgedStockItem): { label: string; className: string } | null {
    if (!item.HasExpiryData) return null;
    if (item.IsExpired) return { label: 'EXPIRED', className: 'bg-red-100 text-red-800 ring-1 ring-red-200' };
    if (item.DaysToExpiry != null && item.DaysToExpiry < 30) return { label: `${item.DaysToExpiry}d`, className: 'bg-orange-100 text-orange-800' };
    if (item.DaysToExpiry != null && item.DaysToExpiry < 90) return { label: `${item.DaysToExpiry}d`, className: 'bg-yellow-100 text-yellow-800' };
    return null;
}

interface SortableHeaderProps {
    field: AgedStockSortField;
    label: string;
    align?: 'left' | 'right';
    sortBy: AgedStockSortField;
    sortOrder: AgedStockSortOrder;
    onSort: (f: AgedStockSortField) => void;
}

function SortableHeader({ field, label, align = 'left', sortBy, sortOrder, onSort }: SortableHeaderProps) {
    const active = sortBy === field;
    return (
        <th
            scope="col"
            className={`whitespace-nowrap px-2 py-2 text-xs font-semibold text-gray-700 ${align === 'right' ? 'text-right' : 'text-left'}`}
        >
            <button
                type="button"
                onClick={() => onSort(field)}
                className="inline-flex items-center gap-1 hover:text-blue-700"
            >
                <span>{label}</span>
                {active && (sortOrder === 'ASC' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
            </button>
        </th>
    );
}

export function AgedStockTable({
    items, sortBy, sortOrder, canEdit, onSort,
    onShowDetail, onShowTransactions, onShowBuyers, onToggleFlag, onFilterByValue, selectedKey,
}: AgedStockTableProps) {
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; items: ContextMenuItem[] } | null>(null);

    const handleContextMenu = useCallback((item: AgedStockItem, e: React.MouseEvent, filterAction?: AgedStockFilterAction) => {
        e.preventDefault();
        e.stopPropagation();

        const menuItems: ContextMenuItem[] = [
            {
                label: 'Show Lot Details',
                icon: <BarChart3 className="h-3.5 w-3.5" />,
                onClick: () => onShowDetail(item),
            },
        ];

        if (filterAction && onFilterByValue) {
            menuItems.push({
                label: `Filter by "${filterAction.label}"`,
                icon: <Filter className="h-3.5 w-3.5" />,
                onClick: () => onFilterByValue(filterAction),
            });
        }

        menuItems.push({
            label: 'Show Transactions (this Lot)',
            icon: <ArrowRightLeft className="h-3.5 w-3.5" />,
            onClick: () => onShowTransactions(item),
        });

        menuItems.push({
            label: 'Show Buyers (Product)',
            icon: <Users className="h-3.5 w-3.5" />,
            onClick: () => onShowBuyers(item),
        });

        setContextMenu({ x: e.clientX, y: e.clientY, items: menuItems });
    }, [onShowDetail, onShowTransactions, onShowBuyers, onFilterByValue]);

    return (
        <>
            {contextMenu && (
                <ContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenu.items}
                    onClose={() => setContextMenu(null)}
                />
            )}
            <div className="overflow-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <SortableHeader field="companyCode"     label="Co."       sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="productCode"     label="Code"      sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="productName"     label="Product"   sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="lotCode"         label="Lot"       sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="warehouseName"   label="Warehouse" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="vendorName"      label="Vendor"    sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="firstPurchaseDate" label="First Purchase" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="ageDays"         label="Age"       sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="currentStockQty" label="Stock Qty" align="right" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="currentStockValueEUR" label="Value EUR" align="right" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortableHeader field="expirationDate"  label="Expiry"    sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <th scope="col" className="whitespace-nowrap px-2 py-2 text-xs font-semibold text-gray-700 text-center">Flags</th>
                            <th scope="col" className="whitespace-nowrap px-2 py-2 text-xs font-semibold text-gray-700 text-center">Comments</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {items.length === 0 && (
                            <tr><td colSpan={13} className="px-4 py-8 text-center text-sm text-gray-500">
                                No aged stock lots match the current filters.
                            </td></tr>
                        )}
                        {/* colSpan unchanged because we removed Last Sale (-1) and added Vendor (+1) */}
                        {items.map(item => {
                            const key = `${item.ItemID}|${item.LotCode}`;
                            const expiry = expiryBadge(item);
                            return (
                                <tr
                                    key={key}
                                    onClick={() => onShowDetail(item)}
                                    onContextMenu={e => handleContextMenu(item, e)}
                                    className={`cursor-pointer hover:bg-blue-50 ${selectedKey === key ? 'bg-blue-50' : ''}`}
                                >
                                    <td
                                        className="whitespace-nowrap px-2 py-2 text-xs text-gray-700"
                                        onContextMenu={e => handleContextMenu(item, e, { filterKey: 'companyCode', value: item.CompanyCode, label: item.CompanyCode })}
                                    >{item.CompanyCode}</td>
                                    <td
                                        className="whitespace-nowrap px-2 py-2 text-xs text-gray-600"
                                        onContextMenu={e => handleContextMenu(item, e, { filterKey: 'search', value: item.ProductCode, label: item.ProductCode })}
                                    >{item.ProductCode}</td>
                                    <td className="px-2 py-2 text-xs text-gray-900 max-w-xs truncate" title={item.ProductName ?? ''}>
                                        {item.ProductName ?? '—'}
                                    </td>
                                    <td
                                        className="whitespace-nowrap px-2 py-2 text-xs"
                                        onContextMenu={e => handleContextMenu(item, e, { filterKey: 'lotSearch', value: item.LotCode, label: `Lot: ${item.LotCode}` })}
                                    >
                                        <code className="rounded bg-gray-100 px-1 py-0.5 text-gray-800">{item.LotCode}</code>
                                    </td>
                                    <td
                                        className="px-2 py-2 text-xs text-gray-700 max-w-[180px] truncate"
                                        title={item.WarehouseName ? `${item.WarehouseName} (${item.WarehouseCode})` : item.WarehouseCode}
                                        onContextMenu={e => handleContextMenu(item, e, { filterKey: 'warehouseCode', value: item.WarehouseCode, label: item.WarehouseName ?? item.WarehouseCode })}
                                    >
                                        {item.WarehouseName
                                            ? <>{item.WarehouseName} <span className="text-gray-400">({item.WarehouseCode})</span></>
                                            : item.WarehouseCode}
                                    </td>
                                    <td
                                        className="px-2 py-2 text-xs text-gray-700 max-w-[160px] truncate"
                                        title={item.VendorName ? `${item.VendorName}${item.SupplierName && item.SupplierName !== item.VendorName ? ` (master supplier: ${item.SupplierName})` : ''}` : (item.SupplierName ?? '')}
                                        onContextMenu={e => handleContextMenu(item, e, { filterKey: 'vendorID', value: item.VendorID ?? '', label: item.VendorName ?? '' })}
                                    >{item.VendorName ?? '—'}</td>
                                    <td className="whitespace-nowrap px-2 py-2 text-xs text-gray-700">{formatDate(item.FirstPurchaseDate)}</td>
                                    <td className={`whitespace-nowrap px-2 py-2 text-xs ${ageColorClass(item.AgeDays)}`}>
                                        {item.AgeDays != null
                                            ? <>
                                                {item.AgeDays}d
                                                <span className={`ml-1 inline-block rounded px-1 py-0.5 text-[10px] ${ageBucketBadge(item.AgeBucket)}`}>
                                                    {item.AgeBucket}
                                                </span>
                                              </>
                                            : '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 text-xs text-right text-gray-900">
                                        {formatNumber(item.CurrentStockQty, 2)}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 text-xs text-right text-gray-900">
                                        {formatNumber(item.CurrentStockValueEUR)}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 text-xs">
                                        {item.HasExpiryData ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Calendar className="h-3 w-3 text-gray-400" />
                                                {formatDate(item.ExpirationDate)}
                                                {expiry && (
                                                    <span className={`rounded px-1 py-0.5 text-[10px] font-semibold ${expiry.className}`}>
                                                        {expiry.label}
                                                    </span>
                                                )}
                                            </span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 text-center">
                                        <span className="inline-flex items-center gap-1">
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); if (canEdit) onToggleFlag(item, 'Expired'); }}
                                                disabled={!canEdit}
                                                className={`rounded p-1 ${item.HasExpiredFlag ? 'bg-red-100 text-red-700' : 'text-gray-400 hover:bg-gray-100'} disabled:cursor-not-allowed`}
                                                title={item.HasExpiredFlag ? 'Expired flag set — click to remove' : 'Flag as Expired'}
                                            >
                                                <AlertTriangle className="h-4 w-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={e => { e.stopPropagation(); if (canEdit) onToggleFlag(item, 'QualityIssue'); }}
                                                disabled={!canEdit}
                                                className={`rounded p-1 ${item.HasQualityIssueFlag ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:bg-gray-100'} disabled:cursor-not-allowed`}
                                                title={item.HasQualityIssueFlag ? 'Quality issue flag set — click to remove' : 'Flag Quality Issue'}
                                            >
                                                <AlertCircle className="h-4 w-4" />
                                            </button>
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap px-2 py-2 text-center text-xs">
                                        <span className="inline-flex items-center gap-1 text-gray-700">
                                            <MessageSquare className="h-3 w-3 text-gray-400" />
                                            {item.CommentCount}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}
